import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  Settings,
  Grid,
  Maximize2,
  Play,
  Pause,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useAnimationStore } from '../../store/animationStore';
import { useAnimationPlayback } from '../../hooks/useAnimationPlayback';
import { cn } from '../../utils/cn';

interface AnimationPreviewProps {
  className?: string;
}

const MIN_ZOOM = 25;
const MAX_ZOOM = 400;
const ZOOM_STEP = 25;
const GRID_SIZE = 20;
const OBJECT_SIZE = 80;

export const AnimationPreview: React.FC<AnimationPreviewProps> = ({ className }) => {
  const {
    selectedAnimationId,
    animations,
    onionSkin,
    playback,
    play,
    pause,
    stop,
    showGrid,
    toggleGrid,
  } = useAnimationStore();

  const { getValuesAtCurrentTime, currentFrame } = useAnimationPlayback();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(100);
  const [showSettings, setShowSettings] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 250, height: 200 });

  const animation = selectedAnimationId ? animations[selectedAnimationId] : null;
  const { isPlaying, fps, currentTime } = playback;
  const totalFrames = Math.round((animation?.duration || 1) * fps);

  // Handle zoom
  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const handleReset = useCallback(() => {
    setZoom(100);
  }, []);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  // Handle stop/reset
  const handleStop = useCallback(() => {
    stop();
  }, [stop]);

  // Resize observer for canvas
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setCanvasSize({
          width: Math.max(100, width),
          height: Math.max(100, height),
        });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Draw preview canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvasSize;
    const scale = zoom / 100;

    // Clear canvas
    ctx.fillStyle = '#18181b'; // zinc-900
    ctx.fillRect(0, 0, width, height);

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = '#27272a'; // zinc-800
      ctx.lineWidth = 1;

      const gridStep = GRID_SIZE * scale;
      const offsetX = (width / 2) % gridStep;
      const offsetY = (height / 2) % gridStep;

      // Vertical lines
      for (let x = offsetX; x < width; x += gridStep) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      // Horizontal lines
      for (let y = offsetY; y < height; y += gridStep) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      // Center cross
      ctx.strokeStyle = '#3f3f46'; // zinc-700
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(width / 2, 0);
      ctx.lineTo(width / 2, height);
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();
    }

    // Get current animated values
    const values = getValuesAtCurrentTime();

    // Center of canvas
    const centerX = width / 2;
    const centerY = height / 2;

    // Draw onion skin frames (previous frames)
    if (onionSkin.enabled && onionSkin.showPrevious && animation) {
      const frameTime = 1 / fps;
      for (let i = 1; i <= onionSkin.previousCount; i++) {
        const opacity = onionSkin.previousOpacity * (1 - (i - 1) / onionSkin.previousCount);
        const frameOffset = -i * frameTime;
        const pastTime = Math.max(0, currentTime + frameOffset);

        // Get values at past time (simplified - using current transform for demo)
        const offsetX = Math.sin(pastTime * Math.PI * 2) * 30 * scale;
        const offsetY = Math.cos(pastTime * Math.PI * 2) * 20 * scale;

        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.translate(centerX + offsetX, centerY + offsetY);
        ctx.fillStyle = onionSkin.previousTint;
        ctx.fillRect(
          -OBJECT_SIZE * scale / 2,
          -OBJECT_SIZE * scale / 2,
          OBJECT_SIZE * scale,
          OBJECT_SIZE * scale
        );
        ctx.restore();
      }
    }

    // Draw onion skin frames (next frames)
    if (onionSkin.enabled && onionSkin.showNext && animation) {
      const frameTime = 1 / fps;
      for (let i = 1; i <= onionSkin.nextCount; i++) {
        const opacity = onionSkin.nextOpacity * (1 - (i - 1) / onionSkin.nextCount);
        const frameOffset = i * frameTime;
        const futureTime = Math.min(animation.duration, currentTime + frameOffset);

        // Get values at future time (simplified - using current transform for demo)
        const offsetX = Math.sin(futureTime * Math.PI * 2) * 30 * scale;
        const offsetY = Math.cos(futureTime * Math.PI * 2) * 20 * scale;

        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.translate(centerX + offsetX, centerY + offsetY);
        ctx.fillStyle = onionSkin.nextTint;
        ctx.fillRect(
          -OBJECT_SIZE * scale / 2,
          -OBJECT_SIZE * scale / 2,
          OBJECT_SIZE * scale,
          OBJECT_SIZE * scale
        );
        ctx.restore();
      }
    }

    // Draw main animated object (placeholder rectangle)
    ctx.save();

    // Apply transforms from animated values
    let translateX = 0;
    let translateY = 0;
    let rotation = 0;
    let scaleX = 1;
    let scaleY = 1;
    let alpha = 1;

    // Extract values from all tracked objects
    Object.values(values).forEach((objValues) => {
      if (objValues['position.x'] !== undefined) {
        translateX = objValues['position.x'] as number;
      }
      if (objValues['position.y'] !== undefined) {
        translateY = objValues['position.y'] as number;
      }
      if (objValues['rotation'] !== undefined) {
        rotation = (objValues['rotation'] as number) * Math.PI / 180;
      }
      if (objValues['scale.x'] !== undefined) {
        scaleX = objValues['scale.x'] as number;
      }
      if (objValues['scale.y'] !== undefined) {
        scaleY = objValues['scale.y'] as number;
      }
      if (objValues['alpha'] !== undefined) {
        alpha = objValues['alpha'] as number;
      }
    });

    ctx.globalAlpha = alpha;
    ctx.translate(centerX + translateX * scale, centerY + translateY * scale);
    ctx.rotate(rotation);
    ctx.scale(scaleX, scaleY);

    // Draw object
    const objectWidth = OBJECT_SIZE * scale;
    const objectHeight = OBJECT_SIZE * scale;

    // Fill
    ctx.fillStyle = '#3b82f6'; // blue-500
    ctx.fillRect(-objectWidth / 2, -objectHeight / 2, objectWidth, objectHeight);

    // Border
    ctx.strokeStyle = '#60a5fa'; // blue-400
    ctx.lineWidth = 2;
    ctx.strokeRect(-objectWidth / 2, -objectHeight / 2, objectWidth, objectHeight);

    // Center dot (gizmo)
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(0, 0, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();

    // Draw transform gizmo handles
    ctx.save();
    ctx.translate(centerX + translateX * scale, centerY + translateY * scale);
    ctx.rotate(rotation);

    // X axis (red)
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(40 * scale, 0);
    ctx.stroke();

    // X arrow
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.moveTo(40 * scale, 0);
    ctx.lineTo(35 * scale, -4);
    ctx.lineTo(35 * scale, 4);
    ctx.closePath();
    ctx.fill();

    // Y axis (green)
    ctx.strokeStyle = '#22c55e';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, 40 * scale);
    ctx.stroke();

    // Y arrow
    ctx.fillStyle = '#22c55e';
    ctx.beginPath();
    ctx.moveTo(0, 40 * scale);
    ctx.lineTo(-4, 35 * scale);
    ctx.lineTo(4, 35 * scale);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }, [
    canvasSize,
    zoom,
    showGrid,
    onionSkin,
    animation,
    currentTime,
    fps,
    getValuesAtCurrentTime,
  ]);

  return (
    <div className={cn('flex flex-col h-full bg-zinc-900', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
        <span className="text-sm font-medium text-zinc-300">Preview</span>
        <div className="flex items-center gap-1">
          <button
            className={cn(
              'p-1.5 rounded transition-colors',
              showGrid
                ? 'text-blue-400 bg-blue-500/20'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
            )}
            onClick={toggleGrid}
            title="Toggle Grid"
          >
            <Grid size={14} />
          </button>
          <button
            className={cn(
              'p-1.5 rounded transition-colors',
              showSettings
                ? 'text-blue-400 bg-blue-500/20'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
            )}
            onClick={() => setShowSettings((prev) => !prev)}
            title="Settings"
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      {/* Canvas area */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden"
      >
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          className="absolute inset-0"
        />

        {/* No animation placeholder */}
        {!animation && (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm">
            No animation selected
          </div>
        )}
      </div>

      {/* Zoom controls */}
      <div className="flex items-center justify-center gap-2 px-3 py-2 border-t border-zinc-700">
        <button
          className="p-1 rounded text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleZoomOut}
          disabled={zoom <= MIN_ZOOM}
          title="Zoom Out"
        >
          <ZoomOut size={14} />
        </button>
        <button
          className="px-2 py-0.5 text-xs text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 rounded transition-colors min-w-[50px]"
          onClick={handleReset}
          title="Reset Zoom"
        >
          {zoom}%
        </button>
        <button
          className="p-1 rounded text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleZoomIn}
          disabled={zoom >= MAX_ZOOM}
          title="Zoom In"
        >
          <ZoomIn size={14} />
        </button>
        <button
          className="p-1 rounded text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
          onClick={handleReset}
          title="Fit to View"
        >
          <Maximize2 size={14} />
        </button>
      </div>

      {/* Playback controls */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-700">
        <div className="flex items-center gap-1">
          <button
            className={cn(
              'p-1.5 rounded transition-colors',
              !animation
                ? 'text-zinc-600 cursor-not-allowed'
                : isPlaying
                ? 'text-blue-400 bg-blue-500/20 hover:bg-blue-500/30'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
            )}
            onClick={handlePlayPause}
            disabled={!animation}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <button
            className={cn(
              'p-1.5 rounded transition-colors',
              !animation
                ? 'text-zinc-600 cursor-not-allowed'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
            )}
            onClick={handleStop}
            disabled={!animation}
            title="Reset"
          >
            <RotateCcw size={14} />
          </button>
        </div>

        {/* Frame counter */}
        <div className="text-xs font-mono text-zinc-500">
          {currentFrame} / {totalFrames}
        </div>
      </div>
    </div>
  );
};

export default AnimationPreview;
